<script setup lang="ts">
import { EmptyState, TraceIdAlert, formatDateTime } from '@yc/ui-kit'
import { computed, onMounted, reactive, ref } from 'vue'

import {
  KINDS,
  KIND_LABELS,
  STATUS_LABELS,
  STATUS_TAG,
  createSite,
  deleteSite,
  listSites,
  siteFormError,
  siteInput,
  updateSite,
  type SiteForm,
  type SiteKind,
  type SiteRow,
  type SiteStatus,
} from '../api/sites'

const rows = ref<SiteRow[]>([])
const total = ref(0)
const loading = ref(false)
/** 列表加载失败：分页、搜索态都在这里，一起展示 */
const error = ref<unknown>(null)
/** 删除失败：网点被路径 / 仿真任务引用时后端 409，带 trace_id */
const refError = ref<unknown>(null)
const notice = ref('')

const query = reactive({ keyword: '', status: '', kind: '', city: '', page: 1, size: 20 })

const formOpen = ref(false)
const editing = ref<SiteRow | null>(null)
const saving = ref(false)
const formError = ref<unknown>(null)
const form = reactive<SiteForm>({
  code: '',
  name: '',
  kind: 'depot',
  city: '',
  address: '',
  lng: undefined,
  lat: undefined,
  capacity: undefined,
  status: 'active',
})

/** 校验文案直接取自 api 层（与后端 validate 同源），校验通过才允许提交 */
const formErrorText = computed(() => siteFormError(form))

async function load(noticeText = ''): Promise<void> {
  loading.value = true
  error.value = null
  try {
    const res = await listSites({
      page: query.page,
      size: query.size,
      keyword: query.keyword,
      status: query.status,
      kind: query.kind,
      city: query.city,
    })
    rows.value = res.list
    total.value = res.total
    notice.value = noticeText
  } catch (e) {
    error.value = e
    rows.value = []
  } finally {
    loading.value = false
  }
}

onMounted(() => void load())

function search(): void {
  query.page = 1
  void load()
}

function page(p: number): void {
  query.page = p
  void load()
}

function resetForm(): void {
  Object.assign(form, {
    code: '',
    name: '',
    kind: 'depot' as SiteForm['kind'],
    city: '',
    address: '',
    lng: undefined,
    lat: undefined,
    capacity: undefined,
    status: 'active' as SiteForm['status'],
  })
}

function openCreate(): void {
  editing.value = null
  resetForm()
  formError.value = null
  formOpen.value = true
}

function openEdit(row: SiteRow): void {
  editing.value = row
  Object.assign(form, {
    code: row.code,
    name: row.name,
    kind: row.kind,
    city: row.city,
    address: row.address,
    lng: row.lng,
    lat: row.lat,
    capacity: row.capacity,
    status: row.status,
  })
  formError.value = null
  formOpen.value = true
}

async function save(): Promise<void> {
  if (formErrorText.value) return
  saving.value = true
  formError.value = null
  const input = siteInput({ ...form })
  try {
    if (editing.value) await updateSite(editing.value.id, input)
    else await createSite(input)
    formOpen.value = false
    await load(editing.value ? `网点 ${form.code.trim()} 已更新` : `网点 ${form.code.trim()} 已创建`)
  } catch (e) {
    // 编码重复（409）、经纬度越界（422）都原样把 trace_id 露出来
    formError.value = e
  } finally {
    saving.value = false
  }
}

async function remove(row: SiteRow): Promise<void> {
  notice.value = ''
  error.value = null
  refError.value = null
  try {
    await deleteSite(row.id)
    await load(`已删除网点「${row.code}」`)
  } catch (e) {
    // 网点被路径当起讫点、或被还能运行的仿真任务列进 site_ids 时后端 409
    refError.value = e
  }
}
</script>

<template>
  <div class="yc-site">
    <header class="yc-site__bar">
      <el-input
        v-model="query.keyword"
        class="yc-site__keyword"
        placeholder="编码 / 名称 / 城市"
        clearable
        @keyup.enter="search"
        @clear="search"
      />
      <el-select v-model="query.kind" class="yc-site__ref" placeholder="全部类型" clearable @change="search">
        <el-option v-for="k in KINDS" :key="k" :label="KIND_LABELS[k]" :value="k" />
      </el-select>
      <!-- 后端 city 是精确匹配（`city = ?`），且没有「城市列表」接口，所以用自由文本而不是下拉 -->
      <el-input
        v-model="query.city"
        class="yc-site__ref"
        placeholder="按城市筛选"
        clearable
        @keyup.enter="search"
        @clear="search"
      />
      <el-select v-model="query.status" class="yc-site__ref" placeholder="全部状态" clearable @change="search">
        <el-option v-for="(label, value) in STATUS_LABELS" :key="value" :label="label" :value="value" />
      </el-select>
      <el-button @click="search">查询</el-button>
      <el-button v-perm="'nos:site:create'" type="primary" @click="openCreate">新建网点</el-button>
      <el-button @click="load()">刷新</el-button>
      <span v-if="notice" class="yc-site__notice">{{ notice }}</span>
    </header>

    <div v-if="error || refError" class="yc-site__error">
      <TraceIdAlert :error="error ?? refError" />
    </div>

    <el-card v-loading="loading" class="yc-site__card">
      <el-table v-if="rows.length" :data="rows" row-key="id">
        <el-table-column prop="code" label="编码" min-width="110" />
        <el-table-column label="名称" min-width="150" show-overflow-tooltip>
          <template #default="{ row }">{{ row.name || '—' }}</template>
        </el-table-column>
        <el-table-column label="类型" width="120">
          <template #default="{ row }">
            <el-tag size="small" type="info">{{ KIND_LABELS[row.kind as SiteKind] ?? row.kind }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="城市" min-width="100" show-overflow-tooltip>
          <template #default="{ row }">{{ row.city || '—' }}</template>
        </el-table-column>
        <el-table-column label="地址" min-width="180" show-overflow-tooltip>
          <template #default="{ row }">{{ row.address || '—' }}</template>
        </el-table-column>
        <el-table-column label="经纬度" width="160">
          <template #default="{ row }">
            <span class="yc-site__mono">{{ row.lng }}, {{ row.lat }}</span>
          </template>
        </el-table-column>
        <el-table-column label="日处理能力" width="110" align="right">
          <template #default="{ row }">{{ row.capacity }} 件/日</template>
        </el-table-column>
        <el-table-column label="状态" width="90">
          <template #default="{ row }">
            <el-tag :type="STATUS_TAG[row.status as SiteStatus] ?? 'info'" size="small">
              {{ STATUS_LABELS[row.status as SiteStatus] ?? row.status }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="更新时间" min-width="155">
          <template #default="{ row }">{{ formatDateTime(row.updated_at) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="130" fixed="right">
          <template #default="{ row }">
            <el-button v-perm="'nos:site:update'" link type="primary" @click="openEdit(row)">编辑</el-button>
            <el-popconfirm
              title="确认删除该网点？被路径当起讫点、或被仿真任务参数引用时不能删除。"
              width="320"
              @confirm="remove(row)"
            >
              <template #reference>
                <el-button v-perm="'nos:site:delete'" link type="danger">删除</el-button>
              </template>
            </el-popconfirm>
          </template>
        </el-table-column>
      </el-table>

      <EmptyState
        v-else-if="!loading"
        title="还没有网点"
        description="网点是仿真算法的节点：先建仓库 / 站点 / 转运中心 / 自提柜，再补路径，最后跑仿真推演。"
      >
        <el-button v-perm="'nos:site:create'" type="primary" @click="openCreate">新建网点</el-button>
      </EmptyState>
    </el-card>

    <el-pagination
      v-if="total > query.size"
      class="yc-site__page"
      layout="total, prev, pager, next"
      :total="total"
      :page-size="query.size"
      :current-page="query.page"
      @current-change="page"
    />

    <el-dialog
      v-if="formOpen"
      v-model="formOpen"
      :title="editing ? `编辑网点 · ${editing.code}` : '新建网点'"
      width="560px"
      :close-on-click-modal="false"
    >
      <div v-if="formError" class="yc-site__error">
        <TraceIdAlert :error="formError" />
      </div>

      <el-form label-position="top" @submit.prevent="save">
        <el-form-item label="编码" :error="form.code.trim() ? '' : '请输入网点编码'">
          <el-input v-model="form.code" maxlength="32" placeholder="如 SH01" />
        </el-form-item>

        <el-form-item label="名称">
          <el-input v-model="form.name" maxlength="64" placeholder="如 上海外高桥仓，可留空" />
        </el-form-item>

        <el-form-item label="类型">
          <el-select v-model="form.kind" class="yc-site__full">
            <el-option v-for="k in KINDS" :key="k" :label="KIND_LABELS[k]" :value="k" />
          </el-select>
        </el-form-item>

        <el-form-item label="城市">
          <el-input v-model="form.city" maxlength="64" placeholder="如 上海，可留空（布局仿真按城市分组）" />
        </el-form-item>

        <el-form-item label="地址">
          <el-input v-model="form.address" maxlength="255" placeholder="可留空" />
        </el-form-item>

        <div class="yc-site__grid">
          <el-form-item label="经度（-180 ~ 180）">
            <el-input-number
              v-model="form.lng"
              class="yc-site__full"
              :min="-180"
              :max="180"
              :precision="6"
              :step="0.000001"
              controls-position="right"
            />
          </el-form-item>
          <el-form-item label="纬度（-90 ~ 90）">
            <el-input-number
              v-model="form.lat"
              class="yc-site__full"
              :min="-90"
              :max="90"
              :precision="6"
              :step="0.000001"
              controls-position="right"
            />
          </el-form-item>
        </div>

        <el-form-item label="日处理能力（件/日，布局仿真的负载分母）">
          <!-- 列宽 INT -->
          <el-input-number
            v-model="form.capacity"
            class="yc-site__full"
            :min="0"
            :max="2147483647"
            :precision="0"
            :step="100"
            controls-position="right"
          />
        </el-form-item>

        <el-form-item label="状态">
          <el-radio-group v-model="form.status">
            <el-radio value="active">启用</el-radio>
            <el-radio value="disabled">停用</el-radio>
          </el-radio-group>
          <p class="yc-site__hint">停用的网点不参与仿真取数（params 点名了仍然算），历史引用保留。</p>
        </el-form-item>

        <el-button type="primary" native-type="submit" :loading="saving" :disabled="!!formErrorText">
          {{ editing ? '保存' : '创建' }}
        </el-button>
        <span v-if="formErrorText" class="yc-site__hint yc-site__hint--inline">{{ formErrorText }}</span>
      </el-form>

      <template #footer>
        <el-button @click="formOpen = false">取消</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<style scoped lang="scss">
.yc-site {
  &__bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--yc-space-2);
    margin-bottom: var(--yc-space-4);
  }

  &__keyword {
    width: 220px;
  }

  &__ref {
    width: 140px;
  }

  &__full {
    width: 100%;
  }

  &__mono {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: var(--yc-font-sm);
  }

  &__grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0 var(--yc-space-3);
  }

  &__notice {
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-sm);
  }

  &__error {
    margin-bottom: var(--yc-space-3);
    font-size: var(--yc-font-sm);
  }

  &__page {
    margin-top: var(--yc-space-4);
    justify-content: flex-end;
  }

  &__hint {
    margin: var(--yc-space-1) 0 0;
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-xs);
    line-height: 1.7;

    &--inline {
      margin-left: var(--yc-space-3);
    }
  }
}
</style>
